import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, Lock, Loader2, CheckCircle2, AlertCircle, Eye, EyeOff,
  SlidersHorizontal, Sun, Moon, LogOut, Upload,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { validateEmail, validateFullName } from "@/lib/validation";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { updateProfile, changePassword } from "@/api/auth";
import UploadAgentBuild from "@/components/dashboard/UploadAgentBuild";
import TwoFactorCard from "@/components/dashboard/TwoFactorCard";
import ConfirmWithPassword from "@/components/shared/ConfirmWithPassword";

// Every role that can reach this page — it is mounted at /developer/settings and
// /pm/settings too, and a missing entry fell through to "User", so a developer's own
// settings page badged them as something the product has no such role for.
const ROLE_LABEL = {
  tester: "Tester", developer: "Developer", project_manager: "Project Manager",
  admin: "Admin", superadmin: "SuperAdmin",
};
const NAME_MAX = 255;   // users.name / users.email are String(255)
const ROLE_BADGE = {
  tester: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  developer: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  project_manager: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  admin: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
  superadmin: "bg-gradient-to-r from-brand-500/15 to-fuchsia-500/15 text-brand-700 dark:text-brand-300",
};

const TABS = [
  { id: "profile", label: "Profile", icon: User, hint: "Name & email" },
  { id: "security", label: "Security", icon: Lock, hint: "Password" },
  { id: "preferences", label: "Preferences", icon: SlidersHorizontal, hint: "Theme & display" },
];
// Publishing a build is scoped by WHO uploads it: a company admin replaces their
// own company's agent, a superadmin replaces the platform default that companies
// without one fall back to. The server decides which from the authenticated user,
// so neither can reach another tenant's build.
//
// Still hidden from testers, developers and PMs — the artefact is an executable
// their machines run, so the right to replace it is the right to run code on them.
// It used to sit in TABS unconditionally, in front of every one of them.
const AGENT_BUILD_TAB = { id: "agent-build", label: "Agent build", icon: Upload, hint: "Upload the .exe" };

function Toast({ type, message, onClose }) {
  return (
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className={cn(
            "mb-5 flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm",
            type === "error"
              ? "border-rose-200 bg-rose-50/70 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200"
              : "border-emerald-200 bg-emerald-50/70 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200"
          )}
        >
          {type === "error" ? <AlertCircle size={15} className="shrink-0" /> : <CheckCircle2 size={15} className="shrink-0" />}
          <span className="flex-1">{message}</span>
          <button type="button" onClick={onClose} className="text-xs font-medium opacity-60 hover:opacity-100">✕</button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PasswordInput({ label, value, onChange, error, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">{label}</span>
      <span className="relative block">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={cn(
            "h-11 w-full rounded-xl border border-slate-300 bg-white px-4 pr-11 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white",
            error && "border-rose-400 focus:border-rose-500 focus:ring-rose-500/15"
          )}
        />
        {/* Open eye = readable now, matching PasswordField. The label states the
            action instead, so the button doesn't announce itself as just "button". */}
        <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
          {show ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
      </span>
      {error && <span className="mt-1.5 block text-xs font-medium text-rose-500">{error}</span>}
    </label>
  );
}

const STRENGTH = ["Too short", "Weak", "Fair", "Good", "Strong"];
const STRENGTH_COLOR = ["bg-rose-500", "bg-rose-500", "bg-amber-500", "bg-sky-500", "bg-emerald-500"];
// Thresholds start at the length the form and the server actually require. They
// used to start at 6, so a 7-character password was rated "Weak" — a rating it
// could never earn, because submitting it is refused for being too short.
function scorePw(p) {
  if (!p) return 0;
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  return Math.min(s, 4);
}

function Segmented({ value, onChange, options, id }) {
  return (
    <div className="flex gap-1 rounded-xl bg-slate-100 p-1 dark:bg-white/5">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button key={opt.value} type="button" onClick={() => onChange(opt.value)}
            className={cn("relative flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors", active ? "text-white" : "text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white")}>
            {active && <motion.span layoutId={`set-${id}`} className="absolute inset-0 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600 shadow-soft" transition={{ type: "spring", stiffness: 420, damping: 34 }} />}
            <span className="relative z-10 inline-flex items-center gap-1.5">{opt.icon && <opt.icon size={15} />}{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function ProfileTab() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "" });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [errors, setErrors] = useState({});
  // Changing the EMAIL re-authenticates (the address is the password-reset
  // channel, so moving it is a takeover primitive). The password is collected
  // only when the address actually changes; a name-only save never sees it.
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  // Seeded from `user` at mount, which is a snapshot: a session restored from
  // cache before /auth/me lands, or a refreshUser() after a change elsewhere,
  // used to leave these fields showing a stale value.
  useEffect(() => {
    setForm({ name: user?.name || "", email: user?.email || "" });
  }, [user?.id, user?.name, user?.email]);

  const emailChanged = form.email.trim().toLowerCase() !== (user?.email || "").toLowerCase();

  function validate() {
    // A blank name used to `return` silently — no request, no error, no toast, so
    // Save simply did nothing. A blank email was worse: it was sent, ignored by the
    // server, and reported back as "Profile updated successfully."
    const next = {};
    const nameError = validateFullName(form.name);
    if (nameError) next.name = nameError;
    // The shared validator, the same one signup and invite-acceptance use. This
    // checked only for blank, so a malformed address reached either the browser's
    // native bubble or a server 400 — two error styles this form doesn't use.
    const emailError = validateEmail(form.email);
    if (emailError) next.email = emailError;
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function submit(e) {
    e.preventDefault();
    if (!validate()) return;
    if (emailChanged) { setConfirming(true); return; }
    await persist();
  }

  async function persist(currentPassword) {
    setBusy(true);
    try {
      await updateProfile({
        name: form.name.trim(),
        email: form.email.trim(),
        current_password: currentPassword || undefined,
      });
      await refreshUser();
      setConfirming(false);
      setToast({ type: "success", message: "Profile updated successfully." });
    } catch (err) {
      // Keep the dialog open on a wrong password so it can be retried in place.
      if (!currentPassword) setConfirming(false);
      setToast({ type: "error", message: err.message || "Failed to update profile." });
      throw err;
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <Toast {...(toast || {})} onClose={() => setToast(null)} />
      <div className="card p-6">
        <h3 className="text-base font-semibold text-slate-800 dark:text-white">Profile information</h3>
        <p className="mt-0.5 text-sm text-slate-400">Update how your name and email appear across the app.</p>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          {/* maxLength matches the String(255) columns. Unbounded, an over-length
              value reached the driver and the raw Postgres error was shown in the
              toast as if it were a message for the user. */}
          <Input id="name" label="Full name" placeholder="Your name" maxLength={NAME_MAX} error={errors.name}
                 value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <Input id="email" type="email" label="Email address" placeholder="you@company.com" maxLength={NAME_MAX} error={errors.email}
                 value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
        </div>
        <p className="mt-2 text-xs text-slate-400">
          Changing your email updates it immediately after save, and asks for your
          password first. We&apos;ll let the old address know.
        </p>
      </div>
      <Button type="submit" disabled={busy}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Save changes
      </Button>

      {/* Re-authentication for an email change. The address is where a password
          reset is sent, so moving it hands over the account — a borrowed session
          could do it in two clicks before this. */}
      <ConfirmWithPassword
        open={confirming}
        title="Change your email address?"
        confirmLabel="Change email"
        busy={busy}
        error={confirmError}
        onClose={() => { setConfirming(false); setConfirmError(""); }}
        onConfirm={async (password) => {
          setConfirmError("");
          try {
            await persist(password);
          } catch (err) {
            setConfirmError(err?.message || "Couldn't change your email address.");
          }
        }}
      >
        Your sign-in address will change from <strong>{user?.email}</strong> to{" "}
        <strong>{form.email.trim()}</strong>. Password resets and every notification
        will go to the new address from now on.
      </ConfirmWithPassword>
    </form>
  );
}

function SecurityTab() {
  // A successful change retires every token for this account (server-side
  // token_version bump), so the server hands back a replacement for this tab and
  // we adopt it — otherwise the next request 401s and the user is signed out for
  // having changed their password.
  const { adoptToken } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const score = scorePw(form.new_password);

  async function save(e) {
    e.preventDefault();
    const errs = {};
    if (!form.current_password) errs.current_password = "Required";
    if (form.new_password.length < 8) errs.new_password = "At least 8 characters";
    // #107: nothing caught "changing" to the same password — the server now
    // rejects this too (a client-side string match can't actually prove it's
    // the same password, only the hash check on the backend can).
    else if (form.current_password && form.new_password === form.current_password) {
      errs.new_password = "New password must be different from your current password";
    }
    if (form.new_password !== form.confirm) errs.confirm = "Passwords do not match";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const res = await changePassword({
        current_password: form.current_password,
        new_password: form.new_password,
      });
      if (res?.access_token) adoptToken(res.access_token);
      setForm({ current_password: "", new_password: "", confirm: "" });
      setToast({
        type: "success",
        message: "Password changed. Any other devices signed in to this account have been signed out.",
      });
    } catch (err) {
      setToast({ type: "error", message: err.message || "Failed to change password." });
    } finally {
      setBusy(false);
    }
  }

  // 2FA is a SIBLING of the password form, not a child of it. Nested inside, its
  // 6-digit code box implicitly submitted the password form on Enter, and its
  // buttons (which default to type="submit") fired changePassword as a side effect
  // of clicking "Set up 2FA".
  return (
    <div className="space-y-5">
    <form onSubmit={save} className="space-y-5">
      <Toast {...(toast || {})} onClose={() => setToast(null)} />
      <div className="card space-y-5 p-6">
        <div>
          <h3 className="text-base font-semibold text-slate-800 dark:text-white">Change password</h3>
          <p className="mt-0.5 text-sm text-slate-400">Use a strong, unique password you don&apos;t use elsewhere.</p>
        </div>
        <PasswordInput label="Current password" placeholder="••••••••" value={form.current_password} onChange={(e) => setForm((f) => ({ ...f, current_password: e.target.value }))} error={errors.current_password} />
        <div>
          <PasswordInput label="New password" placeholder="At least 8 characters" value={form.new_password} onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))} error={errors.new_password} />
          {form.new_password && (
            <div className="mt-2">
              <div className="flex gap-1">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className={cn("h-1.5 flex-1 rounded-full transition-colors", i < score ? STRENGTH_COLOR[score] : "bg-slate-200 dark:bg-white/10")} />
                ))}
              </div>
              <p className="mt-1 text-xs text-slate-400">Strength: <span className="font-medium text-slate-600 dark:text-slate-300">{STRENGTH[score]}</span></p>
            </div>
          )}
        </div>
        <PasswordInput label="Confirm new password" placeholder="••••••••" value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))} error={errors.confirm} />
      </div>
      <Button type="submit" disabled={busy}>
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Update password
      </Button>
    </form>
    <TwoFactorCard />
    </div>
  );
}

function PreferencesTab() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { theme, setTheme, textSize, setTextSize, density, setDensity } = useTheme();

  return (
    <div className="space-y-5">
      <div className="card space-y-5 p-6">
        <div>
          <h3 className="text-base font-semibold text-slate-800 dark:text-white">Display</h3>
          <p className="mt-0.5 text-sm text-slate-400">These apply instantly and are saved on this device.</p>
        </div>
        <div className="space-y-1.5">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">Theme</p>
          <Segmented id="theme" value={theme} onChange={setTheme} options={[{ value: "light", label: "Light", icon: Sun }, { value: "dark", label: "Dark", icon: Moon }]} />
        </div>
        <div className="space-y-1.5">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">Text size</p>
          <Segmented id="text" value={textSize} onChange={setTextSize} options={[{ value: "s", label: "Small" }, { value: "m", label: "Medium" }, { value: "l", label: "Large" }]} />
        </div>
        <div className="space-y-1.5">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">Density</p>
          <Segmented id="density" value={density} onChange={setDensity} options={[{ value: "compact", label: "Compact" }, { value: "default", label: "Default" }, { value: "roomy", label: "Roomy" }]} />
        </div>
      </div>

      <div className="card flex flex-col gap-3 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-slate-800 dark:text-white">Session</h3>
          <p className="mt-0.5 text-sm text-slate-400">Sign out of this device.</p>
        </div>
        <button type="button" onClick={() => { logout(); navigate("/login"); }}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-rose-200 px-4 text-sm font-semibold text-rose-600 transition-colors hover:bg-rose-50 dark:border-rose-500/30 dark:hover:bg-rose-500/10">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </div>
  );
}

// `extraTabs` lets a role-specific settings page (e.g. the company Admin) inject
// its own panels — each { id, label, icon, hint, element } — alongside the
// shared Profile / Security / Preferences tabs, keeping one source of truth for
// the account UI. `initialTab` / `subtitle` are optional overrides.
export default function TesterSettings({ extraTabs = [], initialTab, subtitle }) {
  const { user } = useAuth();
  const role = user?.role || "tester";
  const canUploadAgentBuild = role === "superadmin" || role === "admin";
  const allTabs = [...extraTabs, ...TABS, ...(canUploadAgentBuild ? [AGENT_BUILD_TAB] : [])];
  const [tab, setTab] = useState(initialTab || allTabs[0].id);

  const displayName = user?.name || (user?.email ? user.email.split("@")[0] : "User");
  const initials = displayName.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <>
      <PageHeader title="Settings" subtitle={subtitle || "Manage your profile, security and preferences."} />

      {/* profile banner */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card mb-6 flex items-center gap-4 overflow-hidden p-5">
        <span className="relative grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-brand-500 to-fuchsia-500 text-xl font-bold text-white shadow-glow">
          {initials}
          <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full border-2 border-white bg-emerald-500 dark:border-ink-900" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-lg font-bold text-slate-900 dark:text-white">{displayName}</p>
          <p className="truncate text-sm text-slate-400">{user?.email}</p>
          <span className={cn("mt-1.5 inline-block rounded-full px-2.5 py-0.5 text-[0.6875rem] font-bold uppercase tracking-wide", ROLE_BADGE[role] || ROLE_BADGE.tester)}>{ROLE_LABEL[role] || "User"}</span>
        </div>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
        {/* vertical tab nav */}
        <nav className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible">
          {allTabs.map((t) => {
            const active = tab === t.id;
            return (
              <button key={t.id} type="button" onClick={() => setTab(t.id)}
                className={cn("group flex shrink-0 items-center gap-3 rounded-xl px-3.5 py-3 text-left transition-colors lg:w-full", active ? "bg-brand-600 text-white shadow-soft" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5")}>
                <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-lg", active ? "bg-white/20" : "bg-slate-100 text-slate-500 group-hover:text-brand-600 dark:bg-white/10 dark:text-slate-300")}>
                  <t.icon size={17} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{t.label}</span>
                  <span className={cn("block text-xs", active ? "text-white/70" : "text-slate-400")}>{t.hint}</span>
                </span>
              </button>
            );
          })}
        </nav>

        {/* panel */}
        <div className="min-w-0">
          <AnimatePresence mode="wait">
            <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
              {extraTabs.map((et) => (tab === et.id ? <div key={et.id}>{et.element}</div> : null))}
              {tab === "profile" && <ProfileTab />}
              {tab === "security" && <SecurityTab />}
              {tab === "preferences" && <PreferencesTab />}
              {tab === "agent-build" && canUploadAgentBuild && <UploadAgentBuild />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}
